/**
 * Shared time and duration formatting utilities for the learner experience.
 *
 * These functions provide consistent duration and time-of-day formatting across
 * daily stats, session history, revision timelines, and step-level timing displays.
 */

/**
 * Format a duration in milliseconds to a compact human-readable string.
 *
 * Used for learning time displays (e.g. daily stats, session summaries).
 *
 * @example
 * formatDurationMs(0)        // "0 min"
 * formatDurationMs(300_000)  // "5 min"
 * formatDurationMs(5_400_000) // "1h 30m"
 * formatDurationMs(7_200_000) // "2h"
 */
export function formatDurationMs(ms: number): string {
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  return remaining > 0 ? `${hours}h ${remaining}m` : `${hours}h`;
}

/**
 * Format a duration in milliseconds to a precise short string with seconds.
 *
 * Used for step-level timing displays where second-level precision matters.
 *
 * @example
 * formatDurationPrecise(5_000)   // "5s"
 * formatDurationPrecise(150_000) // "2m 30s"
 * formatDurationPrecise(120_000) // "2m"
 */
export function formatDurationPrecise(ms: number): string {
  const secs = Math.round(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  const remainingSecs = secs % 60;
  return remainingSecs > 0 ? `${mins}m ${remainingSecs}s` : `${mins}m`;
}

/**
 * Format the duration between two ISO date strings as a precise short string.
 *
 * Returns an em dash if `completedAt` is null (e.g. abandoned sessions).
 *
 * @example
 * formatDurationBetween('2024-01-01T10:00:00Z', '2024-01-01T10:05:30Z') // "5m 30s"
 * formatDurationBetween('2024-01-01T10:00:00Z', null)                    // "\u2014"
 */
export function formatDurationBetween(startedAt: string, completedAt: string | null): string {
  if (!completedAt) return '\u2014';
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  const totalSecs = Math.round(ms / 1000);
  const mins = Math.floor(totalSecs / 60);
  const secs = totalSecs % 60;
  if (mins === 0) return `${secs}s`;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

/**
 * Format an ISO date string to a locale-aware time-of-day string (e.g. "14:30").
 *
 * Uses the browser's default locale for 2-digit hour and minute formatting.
 *
 * @example
 * formatTimeOfDay('2024-01-01T14:30:00Z') // "14:30" (or "02:30 PM" depending on locale)
 */
export function formatTimeOfDay(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });
}
